import { createInstance, dateReviver } from '@/api';
import { ProductEntityDetailDto, ProductEntityLinkClient, ProductEntityLinkDetailDto } from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { copyProductEntityValues } from '../../common';

export interface ProductEntityLinkStoreState {
    items?: { key: string; value: ProductEntityLinkDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new ProductEntityLinkClient('', createInstance(), dateReviver);

export const useProductEntityLinkStore = defineStore('configuration-product-entity-link-store', {
    state: (): ProductEntityLinkStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        bySpecification: (state) => {
            return (specificationId: number): ProductEntityLinkDetailDto[] => {
                const key = `specification-${specificationId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        updateProductEntityUI(updatedProductEntity: ProductEntityDetailDto) {
            this.items?.forEach((item) => {
                item.value?.forEach((entity) => {
                    copyProductEntityValues(updatedProductEntity, entity.fromEntity);
                    copyProductEntityValues(updatedProductEntity, entity.toEntity);
                });
            });
        },
        async getBySpecification(id: number, reloading: boolean = false) {
            reloading ? (this.reloading = true) : (this.loading = true);
            const key = `specification-${id}`;

            try {
                const response = await client.getBySpecification(id);

                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitylink.detail.error.title'), error, false);
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async createEntity(
            specificationId: number,
            fromEntityId: number,
            toEntityId: number,
            fromAttributeId: number,
            toAttributeId: number,
            relatedSelection: boolean
        ) {
            this.reloading = true;

            try {
                await client.createEntityLink({
                    specificationId,
                    fromEntityId,
                    toEntityId,
                    fromAttributeId,
                    toAttributeId,
                    relatedSelection
                });

                await this.getBySpecification(specificationId, true);
                useAlertStore().setSuccess(i18n.global.t('entitylink.create.success.title'));
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitylink.create.error.title'), error);
            } finally {
                this.reloading = false;
            }
        },
        async updateEntity(
            specificationId: number,
            productEntityLinkId: number,
            fromEntityId: number,
            toEntityId: number,
            productEntityAttributeLinkId: number,
            fromAttributeId: number,
            toAttributeId: number,
            relatedSelection: boolean
        ) {
            this.reloading = true;

            try {
                await client.updateEntityLink(productEntityLinkId, {
                    productEntityAttributeLinkId,
                    fromEntityId,
                    toEntityId,
                    fromAttributeId,
                    toAttributeId,
                    relatedSelection
                });

                await this.getBySpecification(specificationId, true);
                useAlertStore().setSuccess(i18n.global.t('entitylink.update.success.title'));
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitylink.update.error.title'), error);
            } finally {
                this.reloading = false;
            }
        },
        async deleteEntity(specificationId: number, productEntityLinkId: number): Promise<boolean> {
            this.reloading = true;

            try {
                await client.deleteEntityLink(productEntityLinkId);
                await this.getBySpecification(specificationId, true);
                useAlertStore().setSuccess(i18n.global.t('entitylink.delete.success.title'));
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('entitylink.delete.error.title'), error);
            } finally {
                this.reloading = false;
            }
            return false;
        },
        clear(id: number) {
            const key = `specification-${id}`;

            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
